import { Component, forwardRef, Input, OnChanges, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePickerMode } from './models/date-picker-mode.enum';
import { ComponentChanges } from '../../../models/component-changes.model';
import * as moment from 'moment';

/**
 * @ignore
 */
const DatePickerInlineComponentProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatePickerInlineComponent),
  multi: true,
};

/**
 * The value type
 */
type Value = string | Date | moment.Moment;

/**
 * The date picker inline component
 */
@Component({
  selector: 'ideom-date-picker-inline',
  templateUrl: './date-picker-inline.component.html',
  styleUrls: ['./date-picker-inline.component.scss'],
  providers: [DatePickerInlineComponentProvider],
})
export class DatePickerInlineComponent implements OnInit, OnChanges, ControlValueAccessor {
  /**
   * The flag indicating that control is disabled
   */
  @Input() disabled: boolean;

  /**
   * The timezone offset. If `undefined` - local will be used
   */
  @Input() offset: number | undefined = undefined;

  /**
   * The flag indicating that we should have the range dates
   */
  @Input() isRange: boolean;

  /**
   * The minimum date for date picker
   */
  @Input() min: Value;

  /**
   * The maximum date for date picker
   */
  @Input() max: Value;

  /**
   * The date picker view mode
   */
  @Input() mode: DatePickerMode = DatePickerMode.Days;

  /**
   * The years page size
   */
  @Input() yearsPageSize = 9;

  /**
   * The years page size
   */
  @Input() locale: string;

  /**
   * @ignore
   */
  onChange: (value: string | string[]) => void;

  /**
   * @ignore
   */
  onTouched: () => void;

  /**
   * The start date
   */
  start: moment.Moment;

  /**
   * The end date
   */
  end: moment.Moment;

  /**
   * The min date moment
   */
  minMoment: moment.Moment;

  /**
   * The max date moment
   */
  maxMoment: moment.Moment;

  /**
   * The current date
   */
  current: moment.Moment;

  /**
   * The calendar title
   */
  title: string;
  /**
   * @ignore
   */
  DatePickerMode = DatePickerMode;

  /**
   * @ignore
   */
  ngOnInit(): void {
    this.updateTitle();
  }

  /**
   * @ignore
   * @param changes The changes
   */
  ngOnChanges(changes: ComponentChanges<DatePickerInlineComponent>): void {
    if (changes.min) {
      this.minMoment = this.min ? this.createMoment(this.min) : null;
    }
    if (changes.max) {
      this.maxMoment = this.max ? this.createMoment(this.max) : null;
    }
    if (changes.locale && this.locale) {
      moment.locale(this.locale);
    }
  }

  /**
   * @ignore
   */
  registerOnChange(fn: (value: Value | Value[]) => void): void {
    this.onChange = fn;
  }

  /**
   * @ignore
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * @ignore
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * @ignore
   */
  writeValue(value: Value | Value[]): void {
    if (value) {
      if (this.isRange) {
        if (Array.isArray(value)) {
          const [start, end] = value;
          this.start = this.createMoment(start);
          this.end = this.createMoment(end);
        } else {
          throw new Error('Range mode requires array as input value');
        }
      } else {
        this.start = this.createMoment(value as Value);
        this.end = moment(this.start);
      }
      this.current = moment(this.start);
      this.updateTitle();
    }
  }

  /**
   * This method is used for emitting updated value
   */
  onChangeValue(): void {
    if (this.isRange) {
      this.onChange([this.start?.format(), this.end?.format()]);
    } else {
      this.onChange(this.start?.format());
    }
  }

  /**
   * This method is used for changing date picker view mode
   * @param mode
   */
  onChangeMode(mode: DatePickerMode): void {
    this.mode = mode;
    this.updateTitle();
  }

  /**
   * This method is used for handling calendar item click
   */
  onCalendarItemClick(): void {
    switch (this.mode) {
      case DatePickerMode.Months: {
        this.onChangeMode(DatePickerMode.Days);
        break;
      }
      case DatePickerMode.Years: {
        this.onChangeMode(DatePickerMode.Months);
        break;
      }
    }
  }

  /**
   * This method is used for creating Moment from input date
   * @param date The any moment-like value for date to be converted
   * @returns Returns a moment.Moment value for input date
   */
  createMoment(date: Value): moment.Moment {
    const offset = this.offset !== undefined ? this.offset : moment().utcOffset();
    return moment(date).utcOffset(offset);
  }

  /**
   * This method is used for handling header click
   */
  onHeaderClick(): void {
    if (this.disabled) {
      return;
    }

    switch (this.mode) {
      case DatePickerMode.Days: {
        this.onChangeMode(DatePickerMode.Months);
        break;
      }
      case DatePickerMode.Months: {
        this.onChangeMode(DatePickerMode.Years);
        break;
      }
    }
  }

  /**
   * This method is used for forward action
   */
  onNextClick(): void {
    if (this.disabled) {
      return;
    }

    this.increaseCurrent(1);
  }

  /**
   * This method is used for back action
   */
  onPreviousClick(): void {
    if (this.disabled) {
      return;
    }

    this.increaseCurrent(-1);
  }

  /**
   * This method is used for increasing current date
   * @param amount
   * @private
   */
  private increaseCurrent(amount: number): void {
    switch (this.mode) {
      case DatePickerMode.Days: {
        this.current = moment(this.current).add(amount, 'months').startOf('month');
        break;
      }
      case DatePickerMode.Months: {
        this.current = moment(this.current).add(amount, 'year').startOf('year');
        break;
      }
      case DatePickerMode.Years: {
        this.current = moment(this.current)
          .add(amount * this.yearsPageSize, 'year')
          .startOf('year');
        break;
      }
    }
    this.updateTitle();
  }

  /**
   * This method is used for updating title
   * @private
   */
  private updateTitle(): void {
    switch (this.mode) {
      case DatePickerMode.Days: {
        this.title = this.current?.format('MMMM YYYY');
        break;
      }
      case DatePickerMode.Months:
      case DatePickerMode.Years: {
        this.title = this.current?.format('YYYY');
        break;
      }
    }
  }
}
